// frontend/src/super-admin/support/SupportTicketPage.tsx
// Admin ticket thread: messages, reply, status control.

import * as React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../design-system/components/PageHeader';
import { LoadingState } from '../../design-system/components/LoadingState';
import { ErrorState } from '../../design-system/components/ErrorState';
import { VISUAL, statusPill, type StatusTone } from '../../design-system/visual';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { useAdminPermission } from '../../core/auth/useAdminPermission';
import { ApiError } from '../../core/api-client';
import {
  adminSupportApi,
  type SupportTicket,
  type SupportMessage,
  type TicketStatus,
} from '../../modules/support/api';

const TOPIC_LABELS: Record<string, string> = {
  general: 'General', billing: 'Billing', technical: 'Technical', feature: 'Feature',
};

function ticketTone(status: string): StatusTone {
  if (status === 'open')    return 'info';
  if (status === 'pending') return 'warning';
  return 'muted';
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const STATUS_OPTIONS: Array<{ value: TicketStatus; label: string }> = [
  { value: 'open',    label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'closed',  label: 'Closed' },
];

export function SupportTicketPage() {
  const { id } = useParams<{ id: string }>();
  const canWrite = useAdminPermission('support.write');

  const [ticket,   setTicket]   = React.useState<SupportTicket | null>(null);
  const [messages, setMessages] = React.useState<SupportMessage[]>([]);
  const [loadErr,  setLoadErr]  = React.useState<string | null>(null);
  const [reply,    setReply]    = React.useState('');
  const [sending,  setSending]  = React.useState(false);
  const [updating, setUpdating] = React.useState(false);

  function load() {
    if (!id) return;
    setLoadErr(null);
    setTicket(null);
    adminSupportApi.get(id)
      .then((r) => { setTicket(r.ticket); setMessages(r.messages); })
      .catch((e: unknown) => setLoadErr(String((e as any)?.message ?? e)));
  }

  React.useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submitReply(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !reply.trim()) return;
    setSending(true);
    try {
      const msg = await adminSupportApi.reply(id, reply.trim());
      setReply('');
      setMessages((prev) => [...prev, msg]);
      // refresh ticket status (may have flipped to pending)
      adminSupportApi.get(id).then((r) => setTicket(r.ticket)).catch(() => {});
    } catch (ex) {
      toast.error('Reply failed', { description: ex instanceof ApiError ? ex.message : String((ex as any)?.message ?? ex) });
    } finally {
      setSending(false);
    }
  }

  async function changeStatus(status: TicketStatus) {
    if (!id || !ticket) return;
    setUpdating(true);
    try {
      const updated = await adminSupportApi.setStatus(id, status);
      setTicket(updated);
      toast.success(`Status set to ${status}`);
    } catch (ex) {
      toast.error('Update failed', { description: ex instanceof ApiError ? ex.message : String((ex as any)?.message ?? ex) });
    } finally {
      setUpdating(false);
    }
  }

  if (loadErr) {
    return (
      <div>
        <PageHeader eyebrow="Support" title="Ticket" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <ErrorState description={loadErr} onRetry={load} />
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div>
        <PageHeader eyebrow="Support" title="Ticket" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <LoadingState variant="page" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Support / Tickets"
        title={ticket.subject}
        description={`${ticket.contact_name} · ${ticket.contact_email} · ${TOPIC_LABELS[ticket.topic] ?? ticket.topic}${ticket.tenant_id ? ' · In-app' : ' · Public form'}`}
        actions={
          <Link to="/admin/support">
            <Button variant="ghost" className="h-9 px-3 rounded-full text-[13px] font-semibold gap-1.5">
              <ChevronLeft className="h-4 w-4" />
              Inbox
            </Button>
          </Link>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-5">

        {/* Ticket meta + status control */}
        <section className={[VISUAL.bento, 'p-5 flex flex-wrap items-center gap-4'].join(' ')}>
          <span className={statusPill(ticketTone(ticket.status))}>{ticket.status}</span>
          <span className="text-[12px] text-muted-foreground">
            Opened {new Date(ticket.created_at).toLocaleDateString()}
            {ticket.tenant_id ? ` · Tenant ${ticket.tenant_id.slice(0, 8)}…` : ' · Public'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[12.5px] text-muted-foreground font-medium">Set status:</span>
            {STATUS_OPTIONS.filter((s) => s.value !== ticket.status).map((s) => (
              <button
                key={s.value}
                type="button"
                disabled={updating || !canWrite}
                onClick={() => changeStatus(s.value)}
                className="h-8 px-3 rounded-full text-[12px] font-semibold bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-40"
              >
                {s.label}
              </button>
            ))}
          </div>
        </section>

        {/* Message thread */}
        <section className={[VISUAL.bento, 'p-6'].join(' ')}>
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-4">
            Thread ({messages.length} {messages.length === 1 ? 'message' : 'messages'})
          </h2>

          {messages.length === 0 && (
            <p className="text-[13.5px] text-muted-foreground py-4 text-center">No messages yet.</p>
          )}

          <ul className="space-y-3">
            {messages.map((m) => (
              <li
                key={m.id}
                className={[
                  'rounded-2xl px-4 py-3 text-[13.5px] leading-relaxed',
                  m.author_type === 'admin'
                    ? 'bg-ink-900 text-white ml-8'
                    : 'bg-muted/50 ring-1 ring-border/40 mr-8',
                ].join(' ')}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.10em] mb-1 opacity-60">
                  {m.author_type === 'admin' ? '🛡 ' : ''}{m.author_name}
                  {m.author_email ? ` <${m.author_email}>` : ''}
                  {' · '}{relativeTime(m.created_at)}
                </p>
                <p className="whitespace-pre-wrap">{m.body}</p>
              </li>
            ))}
          </ul>

          {/* Reply form */}
          {ticket.status !== 'closed' && canWrite && (
            <form onSubmit={submitReply} className="space-y-3 pt-5 mt-4 border-t border-border/40">
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Write a reply to the requester…"
                rows={4}
                className="resize-none"
              />
              <Button
                type="submit"
                disabled={sending || !reply.trim()}
                className="rounded-full px-5 h-10 text-[13px] font-semibold"
              >
                {sending ? 'Sending…' : 'Send reply'}
              </Button>
            </form>
          )}

          {ticket.status === 'closed' && (
            <p className="mt-4 pt-4 border-t border-border/40 text-[13px] text-muted-foreground text-center">
              This ticket is closed. Re-open it to reply.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
